/**
 * Browser-side export helpers: a zero-infrastructure "Print / Save as PDF"
 * fallback and a tiny client for the render service.
 *
 * They live here rather than in `@broadpaper/pdf` because they are pure browser
 * code with no Chromium in them, and a designer that imported them from the
 * Chromium backend package made every browser application install
 * `playwright-core` — 14 MB of driver it can never run. `@broadpaper/pdf/client`
 * re-exports this module, so the old import path still works.
 */
import type { DataSource, ReportData, ReportTemplate, Theme } from "@broadpaper/core";
import { resolveWatermark, watermarkCss } from "@broadpaper/license";
import { documentToHtml } from "./preview.js";
import type { LayoutResult } from "./layout.js";

export interface PrintOptions {
  title?: string;
  /** Called after the print dialog closes (best effort). */
  onDone?: () => void;
  /** A licence for this print only, overriding whatever `configure` was given. */
  license?: string | null;
}

/**
 * Opens the laid-out document in a hidden iframe and triggers the browser's
 * print dialog. Because the pages are fixed boxes, the output matches the
 * server-rendered PDF (subject to the browser's own PDF writer).
 *
 * "Save as PDF" in that dialog produces a PDF as surely as either backend does,
 * so it carries the evaluation mark on the same terms. The licence check is
 * what makes this asynchronous; the returned promise settles when the dialog
 * has been opened, and ignoring it is fine.
 */
export async function printReport(result: LayoutResult, opts: PrintOptions = {}): Promise<void> {
  const mark = await resolveWatermark(opts.license !== undefined ? { license: opts.license } : {});
  const html = documentToHtml(result, { title: opts.title, ...(mark ? { extraCss: watermarkCss(mark) } : {}) });
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);
  const doc = frame.contentDocument!;
  doc.open();
  doc.write(html);
  doc.close();
  const win = frame.contentWindow!;
  const cleanup = () => {
    setTimeout(() => frame.remove(), 1000);
    opts.onDone?.();
  };
  win.addEventListener("afterprint", cleanup, { once: true });
  const go = () => {
    win.focus();
    win.print();
  };
  const fonts = (doc as Document & { fonts?: FontFaceSet }).fonts;
  if (fonts?.ready) fonts.ready.then(() => setTimeout(go, 50)).catch(go);
  else setTimeout(go, 100);
}

export interface PdfClientOptions {
  /** Base URL of the PDF service, e.g. "https://pdf.internal.example.com". */
  baseUrl: string;
  token?: string;
  fetch?: typeof fetch;
}

export interface PdfRequest {
  template: ReportTemplate;
  data?: ReportData;
  theme?: Partial<Theme>;
  dataSources?: DataSource[];
  now?: Date;
  locale?: string;
  currency?: string;
  timeZone?: string;
  metadata?: { title?: string; author?: string; subject?: string; keywords?: string[] };
  network?: { allowedHosts?: string[] };
}

/** Minimal client for the BroadPaper PDF service. */
export function createPdfClient(opts: PdfClientOptions) {
  const f = opts.fetch ?? fetch;
  return {
    async render(req: PdfRequest): Promise<{ blob: Blob; pages: number; warnings: number }> {
      const res = await f(`${opts.baseUrl.replace(/\/$/, "")}/render`, {
        method: "POST",
        headers: { "content-type": "application/json", ...(opts.token ? { authorization: `Bearer ${opts.token}` } : {}) },
        body: JSON.stringify({ ...req, now: req.now?.toISOString() })
      });
      if (!res.ok) {
        let message = `PDF service responded ${res.status}`;
        try {
          const j = (await res.json()) as { error?: string };
          if (j.error) message = j.error;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      return { blob: await res.blob(), pages: Number(res.headers.get("x-broadpaper-pages") ?? 0), warnings: Number(res.headers.get("x-broadpaper-warnings") ?? 0) };
    },
    async health(): Promise<boolean> {
      try {
        const res = await f(`${opts.baseUrl.replace(/\/$/, "")}/health`);
        return res.ok;
      } catch {
        return false;
      }
    }
  };
}

/** Triggers a download of a Blob in the browser. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
